import { cn } from '@/lib/utils'
import type { LucideIcon } from 'lucide-react'
import type React from 'react'
import { Button } from './button'

export interface TabContent {
	title: string
	description: string
	/** Optional: Override the action button for this specific tab */
	action?: {
		label: string
		onClick: () => void
		icon?: React.ReactNode
	}
	/** Optional: Override the secondary action for this specific tab */
	secondaryAction?: {
		label: string
		onClick: () => void
	}
	/** Optional: Additional action buttons (rendered as secondary buttons) */
	additionalActions?: Array<{
		label: string
		onClick: () => void
		icon?: React.ReactNode
	}>
}

interface EmptyStateProps {
	icon: LucideIcon
	title?: string
	description?: string
	action?: {
		label: string
		onClick: () => void
		icon?: React.ReactNode
		dataTestId?: string
	}
	secondaryAction?: {
		label: string
		onClick: () => void
		dataTestId?: string
	}
	/** Additional action buttons (rendered as secondary buttons) */
	additionalActions?: Array<{
		label: string
		onClick: () => void
		icon?: React.ReactNode
		dataTestId?: string
	}>
	/** Additional CSS classes for the container */
	className?: string
	/** Container height (default: 400px) */
	containerHeight?: number | string
	/** Show border (default: true) */
	showBorder?: boolean
	dataTestId?: string
	/** Tab-specific content mapped by tab key (e.g., { all: {...}, active: {...}, inactive: {...} }) */
	tabContents?: Record<string, TabContent>
	/** Current active tab key (used with tabContents) */
	currentTab?: string
}

export function EmptyState({
	icon: Icon,
	title: providedTitle,
	description: providedDescription,
	action: providedAction,
	secondaryAction: providedSecondaryAction,
	additionalActions: providedAdditionalActions,
	className,
	containerHeight = 400,
	showBorder = true,
	tabContents,
	currentTab,
	dataTestId,
}: EmptyStateProps) {
	const height = typeof containerHeight === 'number' ? `${containerHeight}px` : containerHeight

	// Determine content based on tab or provided props
	let title = providedTitle || 'No data available'
	let description = providedDescription || ''
	let action = providedAction
	let secondaryAction = providedSecondaryAction
	let additionalActions = providedAdditionalActions

	// If tabContents is provided and currentTab matches, use tab-specific content
	if (tabContents && currentTab && tabContents[currentTab]) {
		const tabContent = tabContents[currentTab]
		title = tabContent.title
		description = tabContent.description
		// Tab-specific action overrides provided action
		action = tabContent.action || providedAction
		secondaryAction = tabContent.secondaryAction || providedSecondaryAction
		additionalActions = tabContent.additionalActions || providedAdditionalActions
	}

	return (
		<div
			data-testid={dataTestId}
			className={cn(
				'flex items-center justify-center bg-card',
				showBorder && 'border border-dashed rounded-lg bg-card',
				className,
			)}
			style={{ height }}>
			<div className='text-center px-6 max-w-md'>
				<div className='flex justify-center mb-2'>
					<Icon className='size-(--icon-standalone) text-muted-foreground' />
				</div>

				<h3 className='text-body font-semibold mb-2 text-foreground'>{title}</h3>

				{description && <p className='text-control text-muted-foreground mb-4 leading-relaxed'>{description}</p>}

				{(action || additionalActions) && (
					<div className='flex flex-col gap-2 items-center mb-3'>
						{action && (
							<Button onClick={action.onClick} className='text-control' data-testid={action.dataTestId}>
								{action.icon}
								{action.label}
							</Button>
						)}

						{additionalActions && additionalActions.length > 0 && (
							<div className='flex gap-2'>
								{additionalActions.map(additionalAction => (
									<Button
										key={additionalAction.label}
										onClick={additionalAction.onClick}
										variant='outline'
										className='text-control'
										data-testid={additionalAction.dataTestId}>
										{additionalAction.icon}
										{additionalAction.label}
									</Button>
								))}
							</div>
						)}
					</div>
				)}

				{/* Secondary Action Link */}
				{secondaryAction && (
					<div className='text-control'>
						<span className='text-muted-foreground text-control'>Need help? </span>
						<Button
							variant={'link'}
							className='text-control text-primary hover:underline font-medium px-1 py-0 h-fit'
							onClick={secondaryAction.onClick}
							data-testid={secondaryAction.dataTestId}>
							{secondaryAction.label}
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}
